import type { LotCostEvent } from '../../types/production'

import { getOfflineDb } from '../dbFactory'

export type LotCostEventListParams = {
  farmId?: string
  productionUnitId?: string
  lotId?: string
  categoryPrefix?: string
  costClassification?: string
  sourceEventType?: string
  sourceEventId?: string
  fromRecordedAt?: string
  toRecordedAt?: string
}

export class DexieLotCostEventRepo {
  async list(params: LotCostEventListParams = {}): Promise<LotCostEvent[]> {
    const farmId = params.farmId?.trim() || undefined
    const productionUnitId = params.productionUnitId?.trim() || undefined
    const lotId = params.lotId?.trim() || undefined
    const categoryPrefix = params.categoryPrefix?.trim() || undefined
    const costClassification = params.costClassification?.trim() || undefined
    const sourceEventType = params.sourceEventType?.trim() || undefined
    const sourceEventId = params.sourceEventId?.trim() || undefined
    const fromRecordedAt = params.fromRecordedAt?.trim() || undefined
    const toRecordedAt = params.toRecordedAt?.trim() || undefined

    const db = getOfflineDb()
    const table = db.lotCostEvents

    let base: LotCostEvent[]

    if (farmId && productionUnitId) {
      base = await table.where('[farmId+productionUnitId]').equals([farmId, productionUnitId]).toArray()
    } else if (farmId) {
      base = await table.where('farmId').equals(farmId).toArray()
    } else if (productionUnitId) {
      base = await table.where('productionUnitId').equals(productionUnitId).toArray()
    } else {
      base = await table.toArray()
    }

    const fromTime = fromRecordedAt ? new Date(fromRecordedAt).getTime() : undefined
    const toTime = toRecordedAt ? new Date(toRecordedAt).getTime() : undefined

    return base
      .filter((event) => {
        if (lotId && (event.lotId ?? '') !== lotId) return false
        if (categoryPrefix && event.categoryPrefix !== categoryPrefix) return false
        if (costClassification && event.costClassification !== costClassification) return false
        if (sourceEventType && (event.sourceEventType ?? '') !== sourceEventType) return false
        if (sourceEventId && (event.sourceEventId ?? '') !== sourceEventId) return false

        if (fromTime !== undefined || toTime !== undefined) {
          const time = new Date(event.recordedAt).getTime()
          if (Number.isNaN(time)) return false
          if (fromTime !== undefined && time < fromTime) return false
          if (toTime !== undefined && time > toTime) return false
        }

        return true
      })
      .sort((left, right) => right.recordedAt.localeCompare(left.recordedAt))
  }

  async getById(id: string): Promise<LotCostEvent | undefined> {
    return getOfflineDb().lotCostEvents.get(id)
  }

  async upsert(event: LotCostEvent): Promise<void> {
    await getOfflineDb().lotCostEvents.put(event)
  }

  async upsertMany(events: LotCostEvent[]): Promise<void> {
    if (events.length === 0) return
    await getOfflineDb().lotCostEvents.bulkPut(events)
  }

  async delete(id: string): Promise<void> {
    await getOfflineDb().lotCostEvents.delete(id)
  }

  async deleteMany(ids: string[]): Promise<void> {
    if (ids.length === 0) return
    await getOfflineDb().lotCostEvents.bulkDelete(ids)
  }

  async clear(): Promise<void> {
    await getOfflineDb().lotCostEvents.clear()
  }
}

export const dexieLotCostEventRepo = new DexieLotCostEventRepo()
